import type { ScheduleCellAssignment } from '../../lib/types';
import {
  daysBetween,
  formatLongDayMonth,
  formatShort,
  formatShortWeekday,
  spanDays,
} from '../../lib/date';

/**
 * How the card talks about dates.
 *
 * - `day` — a specific day is already on screen (a day-navigator header on `/schedule` or
 *   `/project-schedule`), so a single-day assignment says nothing about dates at all and a
 *   multi-day one says where in its span today falls: `Jul 30 – Aug 3 · Day 1 of 5`.
 * - `range` — the card stands alone in an agenda (`/me`), so it always states its own
 *   dates: `Thu, Jul 30` or `Jul 30 – Aug 3 · 5 days`.
 */
export type AssignmentDateMode = 'day' | 'range';

export interface AssignmentCardProps {
  assignment: ScheduleCellAssignment;
  dateMode: AssignmentDateMode;
  /**
   * The `yyyy-MM-dd` day being shown. Required by `dateMode="day"` to work out "Day 3 of 5";
   * ignored in `range` mode.
   */
  day?: string;
  /** Opens the assignment detail sheet. The whole card is the target. */
  onClick: () => void;
  /**
   * Mark an assignment that started before the visible window. `/me` sets this so a start
   * date in the past doesn't read as a data error.
   */
  inProgress?: boolean;
}

interface Badge {
  key: string;
  label: string;
  className: string;
}

/**
 * The exception badges — and only the exceptions. `Billable` is the default state, so a
 * badge on ninety percent of cards would be noise; `categoryName` is the least actionable
 * string on the card and lives in the detail sheet instead.
 *
 * A PTO-equivalent job is non-billable by definition, so it suppresses the `Non-billable`
 * badge rather than stacking two badges on one fact.
 */
function badgesFor(a: ScheduleCellAssignment, inProgress: boolean): Badge[] {
  const badges: Badge[] = [];
  if (a.isPtoEquivalent) {
    badges.push({
      key: 'pto',
      label: 'PTO-equivalent',
      className: 'bg-brand-orange/10 text-brand-orange',
    });
  } else if (!a.isBillable) {
    badges.push({
      key: 'nonbillable',
      label: 'Non-billable',
      className: 'bg-slate-100 text-brand-charcoal',
    });
  }
  if (inProgress) {
    badges.push({
      key: 'inprogress',
      label: 'In progress',
      className: 'bg-brand-blue/10 text-brand-blue',
    });
  }
  if (a.categoryName === 'Uncategorized') {
    badges.push({
      key: 'uncategorized',
      label: 'Uncategorized',
      className: 'bg-slate-100 text-slate-500 border border-dashed border-slate-300',
    });
  }
  return badges;
}

/**
 * Line 3 shows at most two badges. Three can co-occur (PTO-equivalent + In progress +
 * Uncategorized) and, next to a date range, they measure past the 345px available and wrap
 * the card to a fourth line — which breaks the three-line cap the whole card is built on.
 *
 * `badgesFor` returns them in priority order, so the one dropped is always `Uncategorized`:
 * the least operationally urgent of the three for someone reading their own day. It is still
 * announced (the aria-label takes the uncapped list) and still shown in the detail sheet, so
 * capping costs presentation only, never information.
 */
const VISIBLE_BADGE_LIMIT = 2;

/** Line-3 date text, or null when the surrounding context already states the date. */
function dateText(a: ScheduleCellAssignment, mode: AssignmentDateMode, day?: string): string | null {
  if (!a.plannedStart || !a.plannedEnd) return null;
  const span = spanDays(a.plannedStart, a.plannedEnd);

  if (mode === 'range') {
    if (span <= 1) return formatShortWeekday(a.plannedStart);
    return `${formatShort(a.plannedStart)} – ${formatShort(a.plannedEnd)} · ${span} days`;
  }

  // Day mode: the day header already carries the date for a single-day assignment.
  if (span <= 1) return null;
  const range = `${formatShort(a.plannedStart)} – ${formatShort(a.plannedEnd)}`;
  if (!day) return range;
  // Calendar days, not workdays — hence plain "Day 1 of 5".
  const index = daysBetween(a.plannedStart, day) + 1;
  if (index < 1 || index > span) return range;
  return `${range} · Day ${index} of ${span}`;
}

/**
 * The accessible name carries every fact the visual encodes, in reading order — the color
 * rail is decorative and is never announced.
 */
function ariaLabelFor(
  a: ScheduleCellAssignment,
  mode: AssignmentDateMode,
  day: string | undefined,
  badges: Badge[],
): string {
  const parts: string[] = [`${a.jobNumber}, ${a.projectName}.`];

  const context = [a.account, a.serviceLine].filter(Boolean).join(', ');
  if (context) parts.push(`${context}.`);

  if (a.plannedStart && a.plannedEnd) {
    const span = spanDays(a.plannedStart, a.plannedEnd);
    if (span <= 1) {
      parts.push(`${formatLongDayMonth(a.plannedStart)}.`);
    } else {
      const range = `${formatLongDayMonth(a.plannedStart)} to ${formatLongDayMonth(a.plannedEnd)}`;
      const index = mode === 'day' && day ? daysBetween(a.plannedStart, day) + 1 : null;
      parts.push(
        index !== null && index >= 1 && index <= span
          ? `${range}, day ${index} of ${span}.`
          : `${range}, ${span} days.`,
      );
    }
  }

  for (const b of badges) parts.push(`${b.label}.`);
  return parts.join(' ');
}

/**
 * One assignment, as a full-width tappable row. Hard-capped at three lines: identity,
 * context, then dates and exception badges. Everything else — category, region, crew count,
 * projected dates — lives in the detail sheet the card opens.
 *
 * The server-supplied `colorHex` appears only as a 4px rail, never as a filled surface with
 * text on it, so an arbitrary admin color can never wreck the contrast of anything a
 * technician has to read in the sun. The rail carries an inset hairline so a near-white
 * color doesn't vanish against the card.
 */
export function AssignmentCard({
  assignment,
  dateMode,
  day,
  onClick,
  inProgress = false,
}: AssignmentCardProps) {
  const badges = badgesFor(assignment, inProgress);
  const visibleBadges = badges.slice(0, VISIBLE_BADGE_LIMIT);
  const dates = dateText(assignment, dateMode, day);
  const context = [assignment.account, assignment.serviceLine].filter(Boolean).join(' · ');

  const railStyle: React.CSSProperties = {
    backgroundColor: assignment.colorHex,
    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
  };
  if (assignment.isPtoEquivalent) {
    railStyle.backgroundImage =
      'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(255,255,255,0.4) 3px, rgba(255,255,255,0.4) 4px)';
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabelFor(assignment, dateMode, day, badges)}
      className="relative w-full text-left min-h-[64px] bg-white border-b border-slate-200 pl-3 pr-3 py-2.5 active:bg-slate-50"
    >
      <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1" style={railStyle} />

      <span className="block truncate">
        <span className="font-mono text-xs text-slate-500 tabular-nums">{assignment.jobNumber}</span>
        <span className="ml-2 text-[15px] font-semibold text-brand-charcoal">
          {assignment.projectName}
        </span>
      </span>

      {context && <span className="block text-xs text-brand-charcoal/70 truncate">{context}</span>}

      {(dates || visibleBadges.length > 0) && (
        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          {dates && <span className="text-slate-500 tabular-nums">{dates}</span>}
          {visibleBadges.map((b) => (
            <span
              key={b.key}
              className={`inline-flex items-center h-5 px-1.5 rounded text-[11px] font-medium ${b.className}`}
            >
              {b.label}
            </span>
          ))}
        </span>
      )}
    </button>
  );
}
